// admin/socket/rpc.ts — typed Promise wrappers for socket RPC calls.
//
// All RPCs follow the same pattern: emit with an ack callback, resolve on
// `{ ok: true }`, reject with an Error on `{ ok: false, error }`. Each RPC
// has its own timeout so a dead socket can't hang the UI forever.
//
// The socket instance is passed in — we don't reach for a module-level
// singleton here. AdminApp owns the socket; this module is plumbing.

import type { Socket } from "socket.io-client";
import type {
  AdminToggleFlagAck,
  AdminToggleFlagRequest,
  AdminToggleableFlag,
  AdminUser,
  AdminUserSort,
  AdminUsersQueryAck,
  AdminUsersQueryRequest,
} from "../store/types";

const RPC_TIMEOUT_MS = 15_000;

class RpcError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RpcError";
  }
}

function emitWithAck<TAck>(
  socket: Socket,
  event: string,
  payload: unknown,
  timeoutMs = RPC_TIMEOUT_MS,
): Promise<TAck> {
  return new Promise((resolve, reject) => {
    if (!socket.connected) {
      reject(new RpcError("socket_not_connected"));
      return;
    }
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new RpcError("timeout"));
    }, timeoutMs);

    socket.emit(event, payload, (ack: TAck) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      resolve(ack);
    });
  });
}

/**
 * Toggle a flag on a user row. Server validates the viewer is admin, the
 * flag is one of three known names, and the userId isn't the viewer's own
 * when flag === 'is_admin'.
 */
export async function rpcToggleFlag(
  socket: Socket,
  userId: string,
  flag: AdminToggleableFlag,
  enabled: boolean,
): Promise<AdminUser> {
  const req: AdminToggleFlagRequest = { userId, flag, enabled };
  const ack = await emitWithAck<AdminToggleFlagAck>(
    socket,
    "admin:toggle_flag",
    req,
  );
  if (!ack.ok) {
    throw new RpcError(ack.error);
  }
  return ack.user;
}

export interface UsersQueryInput {
  q?: string;
  sort?: AdminUserSort;
  order?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export interface UsersQueryResult {
  requestId: string;
  users: AdminUser[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Paginated user list. Generates a random requestId so the caller can drop
 * stale acks when the user is typing fast (multiple in-flight queries).
 */
export async function rpcUsersQuery(
  socket: Socket,
  input: UsersQueryInput,
): Promise<UsersQueryResult> {
  const requestId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const req: AdminUsersQueryRequest = { requestId, ...input };
  const ack = await emitWithAck<AdminUsersQueryAck>(
    socket,
    "admin:users_query",
    req,
  );
  if (!ack.ok) {
    throw new RpcError(ack.error);
  }
  return {
    requestId: ack.requestId,
    users: ack.users,
    total: ack.total,
    page: ack.page,
    pageSize: ack.pageSize,
  };
}

export { RpcError };
